class ProductCard extends HTMLElement {
  static get observedAttributes () {
    return ['prop-data']
  }

  constructor () {
    super()
    this.elements = {}
    this.data = {}
    this.extraData = {}
  }

  async connectedCallback () {
    this.initializeElements()

    this.setEventListeners()

    const propData = this.getAttribute('data-prop-data')
    const isWishlistCard = this.closest('[data-wishlist-grid-el="grid"]')

    if (propData) {
      this.data = JSON.parse(propData)

      this.renderInitial()
      this.extraData = await this.fetchExtraData(this.data)

      if (this.extraData) {
        this.additionalRender()
      }
    } else if (isWishlistCard != null) {
      this.renderSwatches(true)
    }
  }

  setEventListeners () {
    this.addEventListener('product-card:lazy-load-slides', (e) => {
      if (this.getAttribute('data-lazy-loaded') === 'true') return
      this.lazyLoadSlides()
    })
  }

  lazyLoadSlides () {
    const imageUrls = this.getAttribute('data-img-urls')
    const imageUrlsArr = imageUrls ? JSON.parse(imageUrls) : []

    if (imageUrlsArr.length <= 2) return

    const lazyImages = imageUrlsArr.slice(2)
    const { imageWrapper } = this.elements

    this.setAttribute('data-lazy-loaded', 'true')

    lazyImages.forEach((image) => {
      imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, image, false))
    })
  }

  initializeElements () {
    this.elements = {
      imageWrapper: this.querySelector('[data-product-card-el="images"]'),
      heading: this.querySelector('[data-product-card-el="heading"]'),
      price: this.querySelector('[data-product-card-el="price"]'),
      salePrice: this.querySelector('[data-product-card-el="salePrice"]'),
      regularPrice: this.querySelector('[data-product-card-el="regularPrice"]'),
      percentage: this.querySelector('[data-product-card-el="percentage"]'),
      wishlist: this.querySelector('[data-product-card-el="wishlist"]'),
      quickAdd: this.querySelectorAll('[data-product-card-el="quick-add"]'),
      link: this.querySelector('[data-product-card-el="link"]'),
      slider: this.querySelector('[data-product-card-el="slider"]'),
      variants: this.querySelector('[data-product-card-el="variants"]'),
      singleVariant: this.querySelector('[data-product-card-el="single-variant"]'),
      swatchesInjection: this.querySelector('[data-product-card-el="swatches-injection"]'),
      tag: this.querySelector('[data-product-card-el="tag"]'),
    }
  }

  renderInitial () {
    const { imageWrapper, heading, priceContainer, wishlist, quickAdd, link, variants, swatchesInjection, slider } = this.elements

    let hasAdditionalImages = false

    if (imageWrapper) {
      let lazyload = false

      if (this.index > 6) {
        lazyload = true
      }

      imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, this.data.imageUrl, lazyload))

      hasAdditionalImages = this.data.alternateImageUrls?.length > 0

      if (hasAdditionalImages) {
        imageWrapper.insertAdjacentHTML('beforeend', this.imageTemplate(this.data, this.data.alternateImageUrls[0], true))
      }

      let allImages
 
      if (!this.data.alternateImageUrls) {
        allImages = [this.data.imageUrl]
      } else {
        allImages = [this.data.imageUrl, ...this.data.alternateImageUrls]
      }
      this.setAttribute('data-img-urls', JSON.stringify(allImages))
    }

    if (slider && hasAdditionalImages) {
      slider.setAttribute('data-initialized', 'true')
    }

    if (heading) {
      heading.textContent = this.data.name
    }

    if (wishlist) {
      wishlist.setAttribute('data-product-id', this.data.productId)
      wishlist.setAttribute('data-variant-id', this.data.skus[0].id)
      wishlist.setAttribute('data-product-url', this.data.url)
      wishlist.setAttribute('data-product-title', this.data.name)
      wishlist.setAttribute('data-product-price', this.data.price)
      wishlist.parentElement.setAttribute('data-initialized', 'true')
    }

    if (quickAdd) {
      quickAdd.forEach(el => {
        el.setAttribute('data-product-handle', this.handle)
        el.setAttribute('data-initialized', 'true')
      })
    }

    if (link) {
      link.href = this.data.url
    }

    if (variants) {
      this.renderVariants()

      quickAdd.forEach(el => {
        el.setAttribute('data-initialized', 'true')
      })
    }

    if (swatchesInjection || priceContainer) {
      this.renderSwatches()
    }

    this.setAttribute('data-root', this.data.root)
  }

  renderVariants () {
    const { variants, singleVariant } = this.elements

    if (this.data.skus.length === 1) {
      const singleSku = this.data.skus[0]
      let template = ``

      if (singleSku.inventoryLevel > 0) {
        template = `
          <button data-product-card-el="single-variant" data-product-card-quick-add-el="trigger" data-id="${singleSku.id}" class="button product-card-quick-add__trigger-single">
            Add to Bag
          </button>
        `
      } else {
        template = `
          <a href="${this.data.url}?variant=${singleSku.id}" class="button product-card-quick-add__trigger-single product-card-quick-add__trigger-single--oss">
            Notify me when available
          </a>
        `
      }

      variants.insertAdjacentHTML('afterend', template)

      if (variants) {
        variants.remove()
      }
    } else {
      this.data.skus.forEach(variant => {
        let template = ``

        if (variant.inventoryLevel > 0) {
          template =
          `<li class="product-card-quick-add__listitem">
            <button class="product-card-quick-add__trigger" data-product-card-quick-add-el="trigger" data-id="${variant.id}">
              ${variant.name}
            </button>
          </li>`
        } else {
          template =
          `<li class="product-card-quick-add__listitem">
            <a href="${this.data.url}?variant=${variant.id}" class="product-card-quick-add__trigger product-card-quick-add__trigger--oss">
              ${variant.name}
            </a>
          </li>`
        }

        variants.insertAdjacentHTML('beforeend', template)

        if (singleVariant) {
          singleVariant.remove()
        }
      })
    }
  }

  async renderSwatches (isWishlist) {
    const sectionId = 'product-swatch'

    let productHandle = ''

    if (isWishlist) {
      productHandle = this.dataset.productHandle
    } else {
      productHandle = this.handle
    }

    try {
      const response = await fetch(`/products/${productHandle}?section_id=${sectionId}`)
      if (!response.ok) throw new Error('Failed to fetch swatches section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')

      if (!isWishlist) {
        const priceHtml = html.querySelector('[data-product-card-el="price"]').innerHTML
        this.elements.price.innerHTML = priceHtml
        const salePriceHtml = html.querySelector('[data-product-card-el="salePrice"]').innerHTML
        this.elements.salePrice.innerHTML = salePriceHtml
        const regularPriceHtml = html.querySelector('[data-product-card-el="regularPrice"]').innerHTML
        this.elements.regularPrice.innerHTML = regularPriceHtml
        const percentageHtml = html.querySelector('[data-product-card-el="percentage"]').innerHTML
        this.elements.percentage.innerHTML = percentageHtml
      }

      html.querySelector('[data-product-card-el="price-container"]').remove()

      const swatchesHtml = html.querySelector(`#shopify-section-${sectionId}`).innerHTML
      this.elements.swatchesInjection.innerHTML = swatchesHtml

      const swatchesLoadedEvent = new CustomEvent('GA_SWATCHES_LOADED', {
        detail: {
          swatchesEl: this.elements.swatchesInjection,
        },
      })
      window.dispatchEvent(swatchesLoadedEvent)
    } catch (error) {
      console.error('Failed to fetch swatches section:', error)
    }
  }

  async renderQuickbuy () {
    const sectionId = 'product-card-quick-add'

    const productHandle = this.handle

    try {
      const response = await fetch(`/products/${productHandle}?section_id=${sectionId}`)
      if (!response.ok) throw new Error('Failed to fetch swatches section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const quickaddHtml = html.querySelector('product-card-quick-add')

      this.elements.quickAdd[0].parentNode.appendChild(quickaddHtml)
      this.elements.quickAdd[0].remove()
      this.elements.quickAdd = this.querySelectorAll('[data-product-card-el="quick-add"]')
    } catch (error) {
      console.error('Failed to update quick adds:', error)
    }
  }

  additionalRender () {
    const { tag } = this.elements

    if (tag) {
      this.extraData.tags.forEach((tagText) => {
        if (tagText.includes('TextLabel:')) {
          tag.textContent = tagText.replace('TextLabel:', '')
        }
      })
    }

    if (this.extraData.pantsLengths) {
      this.renderQuickbuy()
    }
  }

  async fetchExtraData (productData) {
    const endpoint = `https://${window.Shopify.shop}/api/2024-04/graphql.json`

    let accessToken

    switch (window.Shopify.shop) {
      case 'victoria-beckham-uk.myshopify.com':
        accessToken = '168f99de7d723da47c230f9c10cf24cb'
        break
      case 'victoria-beckham-international.myshopify.com':
        accessToken = '31cc683cefe9da904e791acdc7ecca64'
        break
      case 'victoria-beckham-us.myshopify.com':
        accessToken = 'fbc6b806071953905a2b3f32ef9421ae'
        break
      default:
        console.log('No access token found')
    }

    const graphqlQuery = `
      query GetProduct($id: ID!) {
        product(id: $id) {
          id
          title
          tags
          variants(first: 10) {
            nodes {
              id
              title
            }
          }
          media(first: 6) {
            edges {
              node {
                id
                alt
                mediaContentType
                previewImage {
                  url
                }
              }
            }
          }
          pantsLengths: metafield(namespace: "shopify", key: "pants-length-type") {
            references(first: 10) {
              nodes {
                ... on Metaobject {
                  id
                  type
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    `

    try {
      const variables = {
        id: `gid://shopify/Product/${productData.productId}`,
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
        },
        body: JSON.stringify({ query: graphqlQuery, variables }),
      })

      if (!response.ok) throw new Error('Failed to fetch product data')
      const data = await response.json()

      return data.data.product || {}
    } catch (error) {
      console.error('Error fetching extra data:', error)
      return {} // Return empty object as fallback
    }
  }

  generateSrcset (url, sizes = [360, 720, 1080]) {
    const [baseUrl, queryParams] = url.split('?')
    const baseWithoutExtension = baseUrl.replace('.jpg', '')
    return sizes.map(size => `${baseWithoutExtension}_${size}x.jpg${queryParams ? `?${queryParams}` : ''} ${size}w`).join(', ')
  }

  imageTemplate (product, imageUrl, lazy) {
    const srcset = this.generateSrcset(imageUrl)
    return `
      <li data-product-card-slider-el="slide" data-ga-el="imageSlide" data-ga-variable-product="${product.name}">
        <img class="product-card__image" ${lazy === true ? 'loading="lazy"' : 'loading="eager"'} src="${imageUrl}" srcset="${srcset}" alt="${product.name}" width="1020" height="680" sizes="(max-width: 767px) 50vw, (min-width: 768px) 25vw">
      </li>
    `
  }

  /**
   * Format money values based on your shop currency settings
   * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
   * or 3.00 dollars
   * @param  {String} [format] - shop money_format setting
   *
   * @return {String} value - formatted value
   */
  formatMoney (cents, format = window.theme.moneyFormat) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '')
    }
    let value = ''
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format

    function formatWithDelimiters (
      number,
      precision = 2,
      thousands = ',',
      decimal = '.',
    ) {
      if (isNaN(number) || number == null) {
        return 0
      }

      number = (number / 100.0).toFixed(precision)

      const parts = number.split('.')
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`,
      )
      const centsAmount = parts[1] ? decimal + parts[1] : ''

      return dollarsAmount + centsAmount
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',')
        break
    }

    return formatString.replace(placeholderRegex, value)
  }

  get handle () {
    return this.data.url.split('products/').pop()
  }

  get index () {
    return Number(this.dataset.index)
  }
}

customElements.define('product-card', ProductCard)
